import React, { useEffect, type FC, type ReactNode } from "react";

import { isApiSessionUser } from "@app/bootstrap/isApiSessionUser";

import { reportError } from "@app/services/error-reporter";

import { redirectToAuth } from "@app/router/redirectToAuth";

import { useAuthStore } from "@app/store/useAuthStore";

import { useReferenceStore } from "@app/store/useReferenceStore";

import { useUserStore } from "@app/store/useUserStore";

import { BootstrapScreen } from "./BootstrapScreen";

import { bootstrapSteps } from "./steps";

import { useAppBootstrap } from "./useAppBootstrap";

export interface SessionGateProps {
    /** Контент приложения (shell), показывается после успешного bootstrap. */
    children: ReactNode;
}

function isSessionDataComplete(): boolean {
    const currentUser = useUserStore.getState().currentUser;
    const { divisionTypes, boTypes, soTypes } = useReferenceStore.getState();
    return isApiSessionUser(currentUser) && divisionTypes.length > 0 && boTypes.length > 0 && soTypes.length > 0;
}

/**
 * Запускает bootstrap сразу после появления token (до рендера shell).
 * При refresh с persisted session data пропускает полную загрузку.
 */

export const SessionGate: FC<SessionGateProps> = ({ children }) => {
    const sessionReady = useAuthStore((state) => state.sessionReady);
    const setSessionReady = useAuthStore((state) => state.setSessionReady);

    const clearSessionState = useAuthStore((state) => state.clearSessionState);
    const currentUser = useUserStore((state) => state.currentUser);

    const { status, currentStep, error, run } = useAppBootstrap();
    const hasCompleteSessionData = isSessionDataComplete();
    const isSessionReady = sessionReady || hasCompleteSessionData;

    useEffect(() => {
        if (!isApiSessionUser(currentUser) || sessionReady) {
            return;
        }

        setSessionReady(true);
    }, [currentUser, sessionReady, setSessionReady]);

    useEffect(() => {
        if (isSessionReady || status !== "idle") {
            return;
        }

        void run(bootstrapSteps);
    }, [isSessionReady, run, status]);

    useEffect(() => {
        if (status === "success") {
            setSessionReady(true);
        }

        if (status === "error" && error != null) {
            reportError(error, { from: "SessionGate" });
            clearSessionState();
            redirectToAuth();
        }
    }, [status, error, setSessionReady, clearSessionState]);

    if (isSessionReady) {
        return <>{children}</>;
    }

    if (status === "idle") {
        return null;
    }

    return <BootstrapScreen currentStep={currentStep} />;
};
